import { ReactElement, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Swiper from '~/ui/components/common/Swiper/Swiper';
import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';

const BathSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$bathSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$bathSection' });

  const slides = [
    {
      id: 1,
      title: t('firstSlideTitle'),
      subtitle: t('firstSlideSubtitle'),
      description: t('firstSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-bath-robes.webp',
      imageHoverSrc: 'images/homePage/banners/hp-bath-robes_hover.webp',
      link: tLink('robes'),
    },
    {
      id: 2,
      title: t('secondSlideTitle'),
      subtitle: t('secondSlideSubtitle'),
      description: t('secondSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-bath-towels.webp',
      imageHoverSrc: 'images/homePage/banners/hp-bath-towels_hover.webp',
      link: tLink('towels'),
    },
    {
      id: 3,
      title: t('thirdSlideTitle'),
      subtitle: t('thirdSlideSubtitle'),
      description: t('thirdSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-bath-kids-robes.webp',
      imageHoverSrc: 'images/homePage/banners/hp-bath-kids-robes_hover.webp',
      link: tLink('kidsRobes'),
    },
    {
      id: 4,
      title: t('fourthSlideTitle'),
      subtitle: t('fourthSlideSubtitle'),
      description: t('fourthSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-bath-mat.webp',
      imageHoverSrc: 'images/homePage/banners/hp-bath-mat_hover.webp',
      link: tLink('bathMat'),
    },
    {
      id: 5,
      title: t('fifthSlideTitle'),
      subtitle: t('fifthSlideSubtitle'),
      description: t('fifthSlideDescription'),
      imageSrc: 'images/homePage/banners/hp-beach-pool.webp',
      imageHoverSrc: 'images/homePage/banners/hp-beach-pool_hover.webp',
      link: tLink('beachPool'),
    },
  ];
  const [firstImgLoaded, setFirstImgLoaded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const renderSlide = (slide: any, index: number): ReactElement => (
    <Link href={slide.link} noTabbing data-event-name="bloc_interact" data-bloc-name={slide.title}>
      <div className="card-title-wrapper">
        <h3>{slide.title}</h3>
        <p className="card-subtitle">{slide.subtitle}</p>
      </div>
      <div className="hover-btn-animation">
        <div
          className="image-container"
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        >
          <img
            loading={firstImgLoaded ? 'eager' : 'lazy'}
            data-src={slide.imageSrc}
            alt={slide.title}
            src={slide.imageSrc}
            onLoad={() => {
              setFirstImgLoaded(true);
            }}
            width="1000"
            height="1022"
          />
          <img
            data-src={slide.imageHoverSrc}
            alt={slide.title}
            src={slide.imageHoverSrc}
            loading="lazy"
            width="1000"
            height="1022"
            className={`hover ${hoveredIndex === index ? 'hover-active' : ''}`}
          />
        </div>
        <div className="hover-content desktop-only">
          <span className="btn base-btn btn-main">
            <span>{tButton('shopNow')}</span>
          </span>
        </div>
      </div>
      <p className="card-description">{slide.description}</p>
    </Link>
  );

  const slideClassName = (): string => 'card';

  return (
    <section id="hp-bath" className="hp-section bath-section bath">
      <div
        className="hp-banner transparent"
        data-event-name="bloc_interact"
        data-bloc-name={t('title')}
      >
        <Link href={tLink('bath')} className="clearfix">
          <picture>
            <source
              data-srcset="images/homePage/mobile/hp-banner-bath-mob.webp"
              media="(max-width: 768px)"
              srcSet="images/homePage/mobile/hp-banner-bath-mob.webp"
            />
            {/* Intentionaly left as jpg: Webp image looked low quality. */}
            <img
              loading="lazy"
              data-src="images/homePage/banners/hp-banner-bath.webp"
              alt={t('alt')}
              src="images/homePage/banners/hp-banner-bath.webp"
              width="1439"
              height="649"
            />
          </picture>
          <div className="stack">
            <h2>{t('title')}</h2>
            <p className="desktop-only">{t('copyDesktop')}</p>
            <p className="mobile-only">{t('copyMobile')}</p>
            <span className="btn base-btn btn-main">
              <span>{t('shopBath')}</span>
            </span>
          </div>
        </Link>
      </div>
      <div className="carousel-wrapper">
        <div className="swiper card-wrapper hp-swiper">
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={slides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3,
                slidesPerGroup: 3,
                spaceBetween: 20,
              },
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default BathSection;
